import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../../features/auth/types/auth.types';
import { localStorageService } from '@/services/localStorage.service';
import { notificationManager } from '@/services/notificationManager.service';
import { v4 as uuidv4 } from 'uuid';
import { authApi } from '@/features/auth/services/auth.service';
import { queryClient } from './QueryProvider';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  visitorId: string;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  logout: () => Promise<void>;
  initializeAuth: (chatbotId?: string | null) => void;
  initializeAuthAsync: (chatbotId?: string | null) => Promise<void>;
  updateCount: (
    key: keyof User['usage'],
    value: number
  ) => void;
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [visitorId, setVisitorId] = useState<string>('');

  const generateVisitorId = () => {
    const newVisitorId = `visitor-${uuidv4()}`;
    const expiry = Date.now() + 60 * 60 * 1000;
    localStorage.setItem('visitorId', newVisitorId);
    localStorage.setItem('visitorIdExpiry', expiry.toString());
    setVisitorId(newVisitorId);
    return newVisitorId;
  };

  const getVisitorId = () => {
    const storedId = localStorage.getItem('visitorId');
    const expiry = localStorage.getItem('visitorIdExpiry');
    if (storedId && expiry && Date.now() < parseInt(expiry)) {
      setVisitorId(storedId);
      return storedId;
    }
    return generateVisitorId();
  };



  const initializeAuth = (chatbotId?: string | null) => {
    setTimeout(async () => {
      const token = localStorageService.getToken()
      const effectiveChatbotId = chatbotId || localStorageService.getChatbotId() || '';
      if (token && effectiveChatbotId) {
        try {
          const currentUser = await authApi.getMe(effectiveChatbotId);
          setUser(currentUser.data);
          // Initialize notifications for logged-in user
          if (currentUser.data?.user.id) {
            await notificationManager.onUserLogin(currentUser.data.user.id);
          }
        } catch (error) {
          console.error('Failed to fetch user:', error);
        }
      }
      getVisitorId();
      setIsLoading(false);
    }, 100);
  };

  const initializeAuthAsync = async(chatbotId?: string | null) => {
      const token = localStorageService.getToken()
      const effectiveChatbotId = chatbotId || localStorageService.getChatbotId() || '';
      if (token && effectiveChatbotId) {
        try {
          const currentUser = await authApi.getMe(effectiveChatbotId);
          setUser(currentUser.data);
          // Initialize notifications for logged-in user
          if (currentUser.data?.user.id) {
            await notificationManager.onUserLogin(currentUser.data.user.id);
          }
        } catch (error) {
          console.error('Failed to fetch user:', error);
        }
      }
      getVisitorId();
      setIsLoading(false);
  };



  useEffect(() => {
    const pathname = window.location.pathname; // "/user/123"
    const parts = pathname.split('/');
    const botId = parts[2] || localStorageService.getChatbotId() || '';

    initializeAuth(botId);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' || e.key === 'user') {
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const logout = async () => {
    try {
      // Clean up notifications before logout
      await notificationManager.onUserLogout();

      // Call backend to revoke refresh token
      await authApi.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with local cleanup even if API call fails
    } finally {
      // Clear local storage and state
      await localStorageService.clearAll();
      setUser(null);
      setTimeout(queryClient.clear, 100);
    }
  };

  const updateCount = (
    key: keyof User['usage'],
    value: number
  ) => {
    setUser((prev) => {
      if (!prev) return prev;

      const limitKeyMap: Record<keyof User['usage'], keyof User['limits']> = {
        messageCreditsUsed: 'messageCreditsLimit',
        characterUsed: 'trainingCharacterLimit',
        crawlWebsitePageUsedCount: 'crawlWebsitePageLimit',
        contactsLimitUsed: 'contactsLimit',
        tagsLimitUsed: 'tagsLimit',
        customAttributesLimitUsed: 'customAttributesLimit',
        automationsLimitUsed: 'automationsLimit',
        agentsLimitUsed: 'agentsLimit',
        appointmentUsersLimitUsed: 'appointmentUsersLimit',
      };

      const limitKey = limitKeyMap[key];
      const maxLimit = prev.limits[limitKey];

      let safeValue = value;

      if (safeValue < 0) safeValue = 0;

      if (safeValue > maxLimit) safeValue = maxLimit;

      return {
        ...prev,
        usage: {
          ...prev.usage,
          [key]: safeValue,
        },
      };
    });
  };


  const value: AuthContextType = {
    user,
    isLoading,
    visitorId,
    setUser,
    setIsLoading,
    logout,
    initializeAuth,
    initializeAuthAsync,
    updateCount,
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}